import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import Constants from "../../Constants";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import ToothFairyButton from "./ToothFairyButton";
import { useTranslation } from "react-i18next";
import useWorkspaces from "../../Hooks/useWorkspaces";
const BatchDownloadModalContent = ({
  type,
  onSelectionChange = () => {},
  enabled = true,
  selection = [],
  handleFormSubmit = () => {},
  filesResult = [],
  context,
}) => {
  const AppToasty = AppToast.useToast();
  const { t, i18n } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { isModelDownloadEnabled } = useWorkspaces();
  const fileAvailable = (item, filesResult) => {
    if (context === "batch" && filesResult?.Contents) {
      const filesInS3 = filesResult?.Contents?.map((u) => {
        const parts = u?.Key?.split("/");
        return parts[parts.length - 1];
      });
      filesInS3.push("input.csv");

      if (filesInS3.includes(item?.filename)) {
        return true;
      }
    } else if (context === "training" && filesResult?.Contents) {
      const filesInS3 = filesResult?.Contents?.map((u) => {
        const key = u?.Key;
        if (!key) return null;

        const parts = key.split("/");

        // Structure: convai-models/workspace-id/training-id/filename
        // or: convai-models/workspace-id/training-id/folder/filename
        // We want everything after index 3 (0=prefix, 1=workspace, 2=training, 3+=file path)
        if (parts.length >= 4) {
          const relativePath = parts.slice(3).join("/");
          return relativePath;
        }
        return parts[parts.length - 1];
      }).filter(Boolean);

      if (filesInS3.includes(item?.filename)) {
        return true;
      }
    }

    return false;
  };
  const baseFiles =
    context === "batch"
      ? Constants.BATCH_DOWNLOAD_OPTIONS
      : Constants.DOWNLOAD_OPTIONS;
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      paddingHorizontal: 24,
      paddingVertical: 16,
    },
    header: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: currentTheme?.theme?.mediumGrey || "#f0f0f0",
    },
    headerText: {
      fontWeight: "600",
      color: currentTheme?.theme?.font_color || "#333",
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 14,
      color: currentTheme?.theme?.mediumGrey || "#666",
    },
    fileList: {
      flex: 1,
      marginBottom: 24,
    },
    fileItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      paddingHorizontal: 16,
      marginBottom: 8,
      backgroundColor: currentTheme?.theme?.backgroundColor || "#fff",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.mediumGrey || "#e5e5e5",
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 1,
    },
    fileItemSelected: {
      borderColor: currentTheme?.theme?.primary || "#007bff",
      // backgroundColor: currentTheme?.theme?.primaryLight || "#f0f8ff",
    },
    fileItemDisabled: {
      opacity: 0.5,
      backgroundColor: currentTheme?.theme?.lightGrey || "#f8f9fa",
    },
    checkboxIcon: {
      marginRight: 12,
    },
    fileItemContent: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    fileName: {
      fontWeight: "bold",
      color: currentTheme?.theme?.font_color || "#333",
      marginBottom: -1,
      marginLeft: 8,
    },
    fileNameDisabled: {
      color: currentTheme?.theme?.darkGrey || "#999",
    },
    fileType: {
      fontSize: 12,
      color: currentTheme?.theme?.mediumGrey || "#666",
      textTransform: "uppercase",
    },
    buttonContainer: {
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: currentTheme?.theme?.mediumGrey || "#f0f0f0",
      alignItems: "center",
    },
    selectionSummary: {
      marginBottom: 16,
      paddingHorizontal: 12,
      paddingVertical: 8,
      backgroundColor: currentTheme?.theme?.lightBlue || "#e3f2fd",
      borderRadius: 6,
      alignSelf: "center",
    },
    selectionText: {
      fontSize: 13,
      color: currentTheme?.theme?.primary || "#007bff",
      fontWeight: "500",
    },
  });

  // Dynamic checkpoint options: every checkpoint-*/adapter_model.safetensors
  // listed in S3 for this run becomes a selectable download. The static
  // Constants list can't know checkpoint names in advance, so these are
  // derived from the live S3 listing; their id IS the relative S3 path and
  // the download handler falls back to using the id as the filename.
  const checkpointOptions = [];
  if (context === "training" && filesResult?.Contents) {
    const seenCheckpoints = new Set();
    (filesResult.Contents || []).forEach((u) => {
      const key = u?.Key || "";
      const parts = key.split("/");
      if (parts.length < 5) return;
      const relative = parts.slice(3).join("/"); // after folder/ws/logId
      const match = relative.match(
        /^(checkpoint-\d+)\/adapter_model\.safetensors$/,
      );
      if (match && !seenCheckpoints.has(match[1])) {
        seenCheckpoints.add(match[1]);
        checkpointOptions.push({
          id: relative,
          label: `${match[1]} (safetensors)`,
          filename: relative,
          type: type,
        });
      }
    });
  }

  const availableFiles = [
    ...baseFiles.filter((i) => i.type === type || i.type === "ALL"),
    ...checkpointOptions,
  ];
  const selectedCount = selection.length;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <AppText style={styles.headerText}>
          {t("downloadModal.selectFiles")}
        </AppText>
        <AppText style={styles.subtitle}>
          {context === "batch"
            ? t("downloadModal.subtitleBatch")
            : t("downloadModal.subtitleTraining")}
        </AppText>
      </View>

      <View style={styles.fileList}>
        {availableFiles.map((i) => {
          const isSelected = selection.includes(i.id);
          const isAvailable = fileAvailable(i, filesResult);
          const isEnabled = enabled && isAvailable;

          return (
            <TouchableOpacity
              key={i.id || i.toString()}
              style={[
                styles.fileItem,
                isSelected && styles.fileItemSelected,
                !isAvailable && styles.fileItemDisabled,
              ]}
              preventTranslation
              onPress={() => {
                if (!isEnabled) return;

                if (!isModelDownloadEnabled() && i.id === "MODEL") {
                  AppToasty.show(t("modelDownloadDisabled"), {
                    placement: "bottom",
                    type: "warning",
                  });
                  return;
                }

                let newSelection;
                if (selection.includes(i.id)) {
                  newSelection = selection.filter((j) => j !== i.id);
                } else {
                  newSelection = [...selection, i.id];
                }
                onSelectionChange(newSelection);
              }}
              disabled={!isEnabled}
            >
              <AppText
                preventTranslation
                wrapperStyle={styles.fileItemContent}
                leftIcon={
                  isSelected
                    ? AppAssets?.icons?.check
                    : AppAssets?.icons?.uncheck
                }
                leftIconColor={
                  isSelected && isEnabled
                    ? currentTheme?.theme?.primary || "#007bff"
                    : currentTheme?.theme?.grey || "#999"
                }
                leftIconStyle={styles.checkboxIcon}
                style={[
                  styles.fileName,
                  !isAvailable && styles.fileNameDisabled,
                ]}
                iconSize={14}
              >
                {i.label || i.toString()}
              </AppText>
              <AppText preventTranslation style={styles.fileType}>
                {i.type} • {i.filename}
              </AppText>
            </TouchableOpacity>
          );
        })}
      </View>

      <View style={styles.buttonContainer}>
        {
          <View style={styles.selectionSummary}>
            <AppText style={[styles.selectionText]}>
              {selectedCount > 0
                ? t("downloadModal.filesSelected", {
                    count: selectedCount,
                    plural: selectedCount !== 1 ? "s" : "o",
                  })
                : t("downloadModal.noFileSelected")}
            </AppText>
          </View>
        }

        <ToothFairyButton
          onPress={handleFormSubmit}
          wrapperStyle={{
            minWidth: 140,
          }}
          title={t("downloadModal.download")}
          disabled={selectedCount === 0}
        />
      </View>
    </View>
  );
};

export default BatchDownloadModalContent;
